import { useState } from "react";
import type { Catalog, ValueDef } from "../../api/types";
import { VALUE_TYPES } from "./model";
import { ValuePicker } from "./ValuePicker";

export function ValuesTable({
  values,
  onChange,
  catalog,
  triggers,
}: {
  values: ValueDef[];
  onChange: (v: ValueDef[]) => void;
  catalog: Catalog | undefined;
  triggers: string[];
}) {
  const [picking, setPicking] = useState(false);
  const [bulk, setBulk] = useState(false);
  const update = (i: number, patch: Partial<ValueDef>) => onChange(values.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= values.length) return;
    const next = [...values];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const existing = new Set(values.map((v) => v.key));
  const aggregates = catalog?.aggregates ?? ["mean", "last", "first", "min", "max", "median", "sum", "count", "ptp", "std", "series", "none"];

  return (
    <div className="col" style={{ padding: 10, flex: 1, minHeight: 0 }}>
      <div className="row">
        <span className="dim small">
          Each row becomes a template token (<code>[code]</code> / <code>v.code</code>). Parameters are aggregated over the snapshot window;
          payload keys, inputs and flight fields are copied as-is.
        </span>
        <span className="spacer" />
        <button className="btn sm" onClick={() => setBulk(!bulk)}>
          {bulk ? "Table view" : "Paste list…"}
        </button>
        <button className="btn sm primary" disabled={!catalog} onClick={() => setPicking(true)}>
          ＋ Add values
        </button>
      </div>
      {bulk ? (
        <textarea
          className="inp mono"
          rows={10}
          defaultValue={values.map((v) => v.key).join("\n")}
          placeholder={"one key per line: ALT, ENG1n1, my_payload_key, inputs.fleet.limit, flight.tail"}
          onBlur={(e) => {
            const keys = e.target.value.split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean);
            const byKey = new Map(values.map((v) => [v.key, v]));
            onChange(
              keys.map(
                (k) =>
                  byKey.get(k) ?? {
                    key: k,
                    code: null,
                    name: null,
                    type: "Analogue",
                    units: null,
                    aggregate: null,
                    format: null,
                    precision: null,
                    source: k.startsWith("inputs.") ? "input" : k.startsWith("flight.") ? "flight" : "auto",
                    conversion_type: null,
                    conversion_table: null,
                    options: {},
                  },
              ),
            );
          }}
        />
      ) : (
        <div style={{ overflow: "auto", flex: 1, minHeight: 0 }}>
          <table className="tbl values-tbl">
            <thead>
              <tr>
                <th />
                <th>Key</th>
                <th>Token (code)</th>
                <th>Source</th>
                <th>Type</th>
                <th>Aggregate</th>
                <th>Format</th>
                <th>Prec.</th>
                <th>Units</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {values.map((v, i) => (
                <tr key={i}>
                  <td className="faint small">
                    <button className="btn ghost sm" onClick={() => move(i, -1)} title="up">
                      ↑
                    </button>
                    <button className="btn ghost sm" onClick={() => move(i, 1)} title="down">
                      ↓
                    </button>
                  </td>
                  <td>
                    <input className="inp sm mono" value={v.key} onChange={(e) => update(i, { key: e.target.value })} style={{ width: 160 }} />
                  </td>
                  <td>
                    <input className="inp sm mono" value={v.code ?? ""} placeholder={v.key} onChange={(e) => update(i, { code: e.target.value || null })} style={{ width: 150 }} />
                  </td>
                  <td>
                    <select className="inp sm" value={v.source} onChange={(e) => update(i, { source: e.target.value as ValueDef["source"] })}>
                      <option value="auto">auto</option>
                      <option value="parameter">parameter</option>
                      <option value="payload">payload</option>
                      <option value="input">input</option>
                      <option value="flight">flight</option>
                    </select>
                  </td>
                  <td>
                    <select className="inp sm" value={v.type} onChange={(e) => update(i, { type: e.target.value })}>
                      {[...new Set([...VALUE_TYPES, v.type])].map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className="inp sm mono"
                      list={`agg-${i}`}
                      value={v.aggregate ?? ""}
                      placeholder={defaultAggregate(v.type)}
                      onChange={(e) => update(i, { aggregate: e.target.value || null })}
                      style={{ width: 130 }}
                      title="built-in name or python:renderers/file.py:func"
                    />
                    <datalist id={`agg-${i}`}>
                      {aggregates.map((a) => (
                        <option key={a} value={a} />
                      ))}
                    </datalist>
                  </td>
                  <td>
                    <input className="inp sm mono" value={v.format ?? ""} placeholder=".2f" onChange={(e) => update(i, { format: e.target.value || null })} style={{ width: 80 }} />
                  </td>
                  <td>
                    <input
                      className="inp sm"
                      type="number"
                      value={v.precision ?? ""}
                      onChange={(e) => update(i, { precision: e.target.value === "" ? null : Number(e.target.value) })}
                      style={{ width: 56 }}
                    />
                  </td>
                  <td>
                    <input className="inp sm" value={v.units ?? ""} onChange={(e) => update(i, { units: e.target.value || null })} style={{ width: 70 }} />
                  </td>
                  <td>
                    <button className="btn ghost sm" onClick={() => onChange(values.filter((_, j) => j !== i))} title="remove">
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
              {!values.length ? (
                <tr>
                  <td colSpan={10} className="dim" style={{ textAlign: "center", padding: 20 }}>
                    No values yet. Add frame parameters, derived parameters, snapshot payload keys, inputs or flight fields.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
      {picking && catalog ? (
        <ValuePicker
          catalog={catalog}
          triggers={triggers}
          existing={existing}
          onClose={() => setPicking(false)}
          onAdd={(vals) => {
            onChange([...values, ...vals.filter((v) => !existing.has(v.key))]);
            setPicking(false);
          }}
        />
      ) : null}
    </div>
  );
}

function defaultAggregate(type: string): string {
  switch (type) {
    case "Analogue":
    case "Discrete":
      return "mean";
    case "Array":
      return "series";
    default:
      return "last";
  }
}
